<section class="ch">
    <div class="ch-inner">
        <header class="ch-head">
            <h1 class="ch-title">Give your AI a <em>memory</em>.</h1>
            <p class="ch-sub">
                LIAM remembers your notes, contacts, meals and receipts inside the AI you already use.
                Pick one to connect. It takes about a minute.
            </p>
        </header>

        <div class="ch-cards">

            <!-- ChatGPT: listed in the app directory, so it really is one tap. -->
            <article class="ch-card gpt">
                <div class="ch-card-top">
                    <span class="ch-mark gpt" aria-hidden="true">◍</span>
                    <div class="ch-card-text">
                        <h2>ChatGPT</h2>
                        <p>Add LIAM from the ChatGPT app directory.</p>
                    </div>
                    <span class="ch-badge">One tap</span>
                </div>
                <a class="ch-btn gpt" [href]="chatgptAppUrl" target="_blank" rel="noopener">
                    Add to ChatGPT <i class="fa-solid fa-arrow-right"></i>
                </a>
            </article>

            <!-- Claude: a custom connector, so the visitor pastes two values. -->
            <article class="ch-card claude" [class.is-open]="open === 'claude'">
                <div class="ch-card-top">
                    <span class="ch-mark claude" aria-hidden="true">✳</span>
                    <div class="ch-card-text">
                        <h2>Claude</h2>
                        <p>Add LIAM as a connector. You paste two values.</p>
                    </div>
                </div>

                <button type="button" class="ch-btn claude ghost" (click)="toggle('claude')"
                    [attr.aria-expanded]="open === 'claude'" aria-controls="ch-claude-steps">
                    {{ open === 'claude' ? 'Hide steps' : 'Connect Claude' }}
                    <i class="fa-solid" [ngClass]="open === 'claude' ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                </button>

                <div class="ch-steps" id="ch-claude-steps" *ngIf="open === 'claude'">
                    <button type="button" class="ch-field" (click)="copyField('name')">
                        <span class="ch-field-label">Name</span>
                        <span class="ch-field-value">{{ connectorName }}</span>
                        <span class="ch-field-copy">
                            <i class="fa-solid" [ngClass]="copiedField === 'name' ? 'fa-check' : 'fa-copy'"></i>
                            {{ copiedField === 'name' ? 'Copied' : 'Copy' }}
                        </span>
                    </button>
                    <button type="button" class="ch-field" (click)="copyField('url')">
                        <span class="ch-field-label">Remote MCP server URL</span>
                        <span class="ch-field-value mono">{{ mcpUrl }}</span>
                        <span class="ch-field-copy">
                            <i class="fa-solid" [ngClass]="copiedField === 'url' ? 'fa-check' : 'fa-copy'"></i>
                            {{ copiedField === 'url' ? 'Copied' : 'Copy' }}
                        </span>
                    </button>

                    <ol class="ch-list">
                        <li>Open Claude. The <b>Add custom connector</b> box opens for you.</li>
                        <li>Paste each value into its field, tap <b>Continue</b>, then <b>Add</b>.</li>
                        <li>Tap <b>Connect</b> and sign in with Google.</li>
                    </ol>
                    <p class="ch-note">On a phone, do this once at claude.ai in your browser. LIAM then shows up in the Claude app too.</p>

                    <a class="ch-btn claude" [href]="claudeConnectorUrl" target="_blank" rel="noopener">
                        Open Claude <i class="fa-solid fa-arrow-right"></i>
                    </a>
                </div>
            </article>

            <!-- Muse: set up by asking it in chat, so the visitor pastes one message. -->
            <article class="ch-card muse" [class.is-open]="open === 'muse'">
                <div class="ch-card-top">
                    <span class="ch-mark muse" aria-hidden="true">◆</span>
                    <div class="ch-card-text">
                        <h2>Muse</h2>
                        <p>Paste one message into Muse. We copy it for you.</p>
                    </div>
                </div>

                <button type="button" class="ch-btn muse ghost" (click)="toggle('muse')"
                    [attr.aria-expanded]="open === 'muse'" aria-controls="ch-muse-steps">
                    {{ open === 'muse' ? 'Hide steps' : 'Connect Muse' }}
                    <i class="fa-solid" [ngClass]="open === 'muse' ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                </button>

                <div class="ch-steps" id="ch-muse-steps" *ngIf="open === 'muse'">
                    <p class="ch-copied" [class.ok]="museCopyOk" aria-live="polite">
                        <ng-container *ngIf="museCopyOk; else copyItYourself">
                            <i class="fa-solid fa-check"></i> Copied to your clipboard
                        </ng-container>
                        <ng-template #copyItYourself>Copy this message</ng-template>
                    </p>
                    <pre class="ch-paste">{{ musePasteBlock }}</pre>

                    <ol class="ch-list">
                        <li>Open Muse and paste this as a new message.</li>
                        <li>Tap <b>Connect</b> on the LIAM card it shows you.</li>
                        <li>Sign in with Google.</li>
                    </ol>

                    <div class="ch-row">
                        <button type="button" class="ch-btn muse ghost small" (click)="copyMuse()">
                            <i class="fa-solid" [ngClass]="copiedField === 'muse' ? 'fa-check' : 'fa-copy'"></i>
                            {{ copiedField === 'muse' ? 'Copied' : 'Copy again' }}
                        </button>
                        <a class="ch-btn muse" [href]="museUrl" target="_blank" rel="noopener">
                            Open Muse <i class="fa-solid fa-arrow-right"></i>
                        </a>
                    </div>
                </div>
            </article>
        </div>

        <footer class="ch-foot">
            <a routerLink="/login" class="ch-link">Already connected? Sign in to see your memories</a>
            <button type="button" class="ch-link" (click)="scrollToStory()">
                Why LIAM <i class="fa-solid fa-arrow-down"></i>
            </button>
        </footer>
    </div>
</section>
